import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import FactCheckIcon from '@mui/icons-material/FactCheckRounded';
import AssignmentIcon from '@mui/icons-material/AssignmentRounded';
import EditNoteIcon from '@mui/icons-material/EditNoteRounded';
import HowToRegIcon from '@mui/icons-material/HowToRegRounded';
import SavingsIcon from '@mui/icons-material/SavingsRounded';
import CheckCircleIcon from '@mui/icons-material/CheckCircleRounded';
import { PRIMARY, PRIMARY_LIGHT } from '../../../theme';

export const PAYROLL_STAGES = [
    { key: 'attendance', label: 'Attendance Cutoff', description: 'Lock monthly attendance', icon: FactCheckIcon },
    { key: 'inputs', label: 'Payroll Inputs', description: 'Reporting days, OT & adjustments', icon: EditNoteIcon },
    { key: 'calculation', label: 'Salary Calculation', description: 'Compute gross / LOP / net', icon: AssignmentIcon },
    { key: 'approval', label: 'Manager Approval', description: 'Review & approve register', icon: HowToRegIcon },
    { key: 'paid', label: 'Salary Credited', description: 'Paid to bank & payslips shared', icon: SavingsIcon },
];

const NODE = 42;
const LINE = 3;
const GREEN = '#16A34A';
const GREEN_BG = '#DCFCE7';
const IDLE = '#CBD5E1';
const IDLE_LINE = '#E5E7EB';

const GAP = 6; // breathing room between a node's edge and the line

// One unbroken line per gap, spanning from this node across into the next
// column. Drawing it as two halves that met at the column boundary left a
// visible seam in the middle. Pinned to the node's vertical centre rather than
// nudged with a margin, so it stays aligned however far the labels below wrap.
function Connector({ done }) {
    return (
        <Box
            aria-hidden
            sx={{
                position: 'absolute',
                top: (NODE - LINE) / 2,
                left: `calc(50% + ${NODE / 2 + GAP}px)`,
                width: `calc(100% - ${NODE + GAP * 2}px)`,
                height: LINE,
                borderRadius: LINE,
                bgcolor: done ? GREEN : IDLE_LINE,
                transition: 'background-color .2s',
            }}
        />
    );
}

/**
 * One payroll cycle stepper for every screen that shows it. `current` is the
 * index of the stage in progress; everything before it counts as done.
 * `meta` optionally carries { [stageKey]: { on, by } } for completed stages.
 */
export default function PayrollCycleStepper({
    stages = PAYROLL_STAGES,
    current = 0,
    meta,
    tone = PRIMARY,
    toneBg = PRIMARY_LIGHT,
}) {
    return (
        <Box sx={{ overflowX: 'auto', pb: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', minWidth: stages.length * 128 }}>
                {stages.map((stage, idx) => {
                    const done = idx < current;
                    const isCurrent = idx === current && current < stages.length;
                    const colour = done ? GREEN : isCurrent ? tone : IDLE;
                    const bg = done ? GREEN_BG : isCurrent ? toneBg : '#fff';
                    const Icon = stage.icon;
                    const info = meta?.[stage.key];
                    const tip = done && (info?.on || info?.by)
                        ? `${info.on || ''}${info.by ? `${info.on ? ' · ' : ''}by ${info.by}` : ''}`
                        : '';

                    const node = (
                        <Box sx={{ position: 'relative', flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', px: 0.5 }}>
                            {idx < stages.length - 1 && <Connector done={idx < current} />}

                            <Box sx={{
                                width: NODE, height: NODE, borderRadius: '50%', flexShrink: 0,
                                bgcolor: bg, border: `2px solid ${colour}`,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                boxShadow: isCurrent ? `0 0 0 4px ${colour}22` : 'none',
                                position: 'relative', zIndex: 1,
                                transition: 'background-color .2s, border-color .2s',
                            }}>
                                {done
                                    ? <CheckCircleIcon sx={{ color: colour, fontSize: 23 }} />
                                    : <Icon sx={{ color: colour, fontSize: 21 }} />}
                            </Box>

                            <Typography sx={{
                                fontSize: 12, fontWeight: 700, mt: 0.9, textAlign: 'center', lineHeight: 1.3,
                                color: done ? GREEN : isCurrent ? tone : '#475569',
                            }}>
                                {stage.label}
                            </Typography>
                            <Typography sx={{ fontSize: 10.5, color: '#9CA3AF', textAlign: 'center', lineHeight: 1.35, mt: 0.15 }}>
                                {stage.description}
                            </Typography>
                            {done && info?.on && (
                                <Typography sx={{ fontSize: 9.5, fontWeight: 700, color: GREEN, textAlign: 'center', mt: 0.3 }}>
                                    {info.on}
                                </Typography>
                            )}
                        </Box>
                    );

                    return (
                        <React.Fragment key={stage.key}>
                            {tip ? <Tooltip arrow title={tip}>{node}</Tooltip> : node}
                        </React.Fragment>
                    );
                })}
            </Box>
        </Box>
    );
}
